/**
 * Suggestions for an aircraft: what it needs next and a command that does it. Follows the sim's
 * own rules: an initial altitude is useful before take-off, an approach the aircraft can actually fly down (lined up,
 * near the glideslope), a handoff within 1 nm of the exit fix at 4 000 ft above the field or
 * higher. `commandHint` is the one-line form; the aircraft card shows `suggestAction` as its lit key. Pure; no DOM.
 */
import { alongTrackM, bearingDeg, crossTrackM, defaultApproach, destination, distanceM, finalJoin, findRunwayEnd, FT_TO_M, NM_TO_M, toMagneticDeg, toTrueDeg, wrap180, type AircraftState, type AirportData } from '@atc/core';

const FT_PER_NM = 6076.12;

/** Heading as the command grammar writes it: three digits, 360 for north. */
export const hdg3 = (deg: number) => String(Math.round(((deg % 360) + 360) % 360) || 360).padStart(3, '0');
/** A vector as the controller says it: the true geometry turned magnetic, in 5° steps. */
const vector3 = (airport: AirportData, trueDeg: number) => hdg3(Math.round(toMagneticDeg(airport, trueDeg) / 5) * 5);
const ft = (n: number) => `${Math.round(n).toLocaleString('en-US')} ft`;

/** What an aircraft needs next, split so the hint line and the Assist panel can each lay it out. */
export interface Suggestion {
  callsign: string;
  /** Plain words: what it needs and why. */
  why: string;
  /** A command that does it, in the typed grammar. */
  command?: string;
  /** Text the hint line shows after the command. */
  tail?: string;
  /**
   * How soon to act: 1 reserved for traffic alerts, 2 waiting on a
   * landing or take-off clearance, 3 departure climb or exit, 4 arrival vectors, 0 nothing needed
   * now (any command is optional, such as a go-around).
   */
  priority: 0 | 1 | 2 | 3 | 4;
}

/**
 * Vectoring to the ILS the way an approach controller does it (PANS-ATM 8.9.4, and the LLBG ILS 12
 * chart): join the final no closer than its intermediate fix (`finalJoin`), level at or below the
 * glideslope, cutting in at 30° (45° at most). An arrival that cannot make that join from where it
 * is flies a downwind on its own side of the final, turns base abeam a longer final, then cuts in.
 *
 * Assumptions: the base leg is 2 nm to the side of the final, abeam the point from which a 30° cut
 * from there joins 1 nm outside the intermediate fix (about 14 nm for LLBG 12); more than 15 nm
 * from that base point an arrival descends to 6 000 ft above the field, closer in to the joining
 * level; more than 6 nm off the centreline it is first sent toward the base point.
 */
const INTERCEPT_DEG = 30;
const MAX_INTERCEPT_DEG = 45;
const BASE_OFFSET_NM = 2;
const FAR_OFF_CENTRELINE_NM = 6;
const FAR_FROM_BASE_NM = 15;
/** Beyond this turn onto the intercept the aircraft swings wide of the centreline and overshoots. */
const MAX_TURN_ONTO_INTERCEPT_DEG = 120;
const tanDeg = (d: number) => Math.tan((d * Math.PI) / 180);

/** Who the player is: in an Academy approach position tower clears to land, after a transfer (ID CTW). */
export interface HintRole { approachRole?: boolean }

export function commandHint(s: AircraftState, airport: AirportData, role: HintRole = {}): string {
  const g = suggestAction(s, airport, role);
  return `${g.why}${g.command ? `: ${g.command}` : ''}${g.tail ?? ''}`;
}

export function suggestAction(s: AircraftState, airport: AirportData, role: HintRole = {}): Suggestion {
  const cs = s.callsign, a = s.assigned ?? {};
  const field = airport.elevationFt;
  const say = (priority: Suggestion['priority'], why: string, command?: string, tail?: string): Suggestion => ({ callsign: cs, why, command, tail, priority });
  switch (s.phase) {
    case 'holdShort': case 'lineUp': case 'linedUp':
      if (a.altitudeFt === undefined) return say(2, `waiting to depart toward ${s.requested || 'its exit'}. Give an altitude for a continuous climb, then take off`, `${cs} C ${Math.max(1, Math.round(airport.initialAltitudeFt / 1000))} T`);
      return s.phase === 'holdShort' ? say(2, `cleared to ${ft(a.altitudeFt)}. Take off`, `${cs} T`, `, or line up and wait: ${cs} W`) : say(2, `cleared to ${ft(a.altitudeFt)}. Take off`, `${cs} T`);
    case 'takeoffRoll': return say(0, 'rolling. Stop the take-off only if you must', `${cs} A`);
    case 'abortRoll': return say(0, 'rejected take-off: stopping, then vacating and taxiing back to the holding point');
    case 'rollout': case 'landed': return say(0, 'landed, rolling out');
    case 'handedOff': case 'exited': case 'cancelled': case 'forcedLanding': return say(0, 'no longer on your frequency');
  }

  if (s.kind === 'departure') {
    const handoffFt = Math.ceil((field + 4000) / 1000) * 1000;
    // On a SID, climb to its published top: some legs (LLBG's TOMAL 4E after ORLEV) wait for it.
    const sid = s.procedure ? [...airport.sids.values()].find((p) => p.name === s.procedure) : undefined;
    const climbFt = Math.max(handoffFt, sid?.altitudeFt ?? 0);
    const steps: string[] = [];
    if ((a.altitudeFt ?? s.altitudeFt) < climbFt) steps.push(`C ${Math.round(climbFt / 1000)}`);
    // Off its SID on a heading: a direct to the exit fix puts it back on course.
    if (s.requested && a.headingDeg !== undefined) steps.push(`C ${s.requested}`);
    const rule = `${sid ? `flying the ${sid.name} departure; ` : ''}hands off within 1 nm of ${s.requested} at ${ft(handoffFt)} or higher`;
    return steps.length ? say(3, rule, `${cs} ${steps.join(' ')}`) : say(0, `${rule}; on its way`);
  }

  // Arrivals.
  const rw = findRunwayEnd(airport, a.runway ?? airport.arrivalRunway);
  if (!rw) return say(0, 'no arrival runway in the airport data');
  if (a.runway) {
    if (s.landingCleared) return role.approachRole ? say(0, `cleared to land ${rw.name} by tower`) : say(0, `cleared to land ${rw.name}. Go around if the runway is not clear`, `${cs} A`);
    if (s.phase === 'final') {
      return role.approachRole
        ? say(2, `established on the runway ${rw.name} ILS. Transfer it to tower for its landing clearance`, `${cs} CTW`)
        : say(2, `established on the runway ${rw.name} ILS. Clear it to land once the runway is free`, `${cs} L ${rw.name}`);
    }
    return say(0, `cleared for the runway ${rw.name} ILS; it holds its heading until it meets the localizer. Cancel with a heading`, `${cs} C ${vector3(airport, s.headingDeg)}`);
  }
  if (a.hold) {
    // Holding at a fix of the published approach, the crew can fly the approach from the hold.
    if (defaultApproach(airport, rw.name)?.legs.some((l) => l.fix === a.hold)) return say(0, `holding at ${a.hold} on the runway ${rw.name} ILS. When the sequence allows, clear the approach from the hold`, `${cs} I ${rw.name}`);
    return say(0, `holding at ${a.hold}. Leave the hold with a heading`, `${cs} C ${vector3(airport, bearingDeg(s.lat, s.lon, rw.lat, rw.lon))}`);
  }

  const join = finalJoin(airport, rw.name);
  const minNm = join?.minNm ?? 10, joinFt = join?.altitudeFt ?? Math.ceil((field + 3000) / 1000) * 1000;
  const thrFt = rw.elevationM / FT_TO_M;
  const slopeFt = (nm: number) => thrFt + tanDeg(rw.glideslopeDeg || 3) * nm * FT_PER_NM;
  const backNm = -alongTrackM(rw.lat, rw.lon, rw.headingDeg, s.lat, s.lon) / NM_TO_M;
  const xtkNm = crossTrackM(rw.lat, rw.lon, rw.headingDeg, s.lat, s.lon) / NM_TO_M;
  // Right of the centreline (looking along the landing direction) is +1: cutting in from there is a turn left of the runway heading.
  const side = xtkNm >= 0 ? 1 : -1;
  const baseNm = minNm + 1 + BASE_OFFSET_NM / tanDeg(INTERCEPT_DEG);
  const abeam = destination(rw.lat, rw.lon, (rw.headingDeg + 180) % 360, baseNm * NM_TO_M);
  const base = destination(abeam.lat, abeam.lon, (rw.headingDeg + 90 * side + 360) % 360, BASE_OFFSET_NM * NM_TO_M);
  const toBase = bearingDeg(s.lat, s.lon, base.lat, base.lon);
  const descendToFt = distanceM(s.lat, s.lon, base.lat, base.lon) / NM_TO_M > FAR_FROM_BASE_NM ? Math.floor((field + 6000) / 1000) * 1000 : joinFt;
  const assignedLowEnough = a.altitudeFt !== undefined && a.altitudeFt <= descendToFt;
  const descend = s.altitudeFt > descendToFt + 100 && !assignedLowEnough ? `C ${descendToFt / 1000}` : '';
  const cmd = (...parts: string[]) => [cs, ...parts.filter(Boolean)].join(' ');
  // A heading already assigned counts as the turn, so it is not suggested again while the aircraft turns.
  // The assigned heading is magnetic, as said; the geometry here is true.
  const heading = a.headingDeg !== undefined ? toTrueDeg(airport, a.headingDeg) : s.headingDeg;
  const turnTo = (deg: number) => (Math.abs(wrap180(heading - deg)) > 10 ? `C ${vector3(airport, deg)}` : '');
  const vector = (why: string, deg: number) => {
    const turn = turnTo(deg);
    return turn || descend ? say(4, why, cmd(descend, turn)) : say(0, why.replace(/^(\w+)/, 'on its way: $1'));
  };
  const clearIls = (why: string, deg: number, joinsAtNm: number) => {
    const turn = turnTo(deg);
    // At or below the glideslope where it joins, so it captures it from below as the chart intends.
    if (s.altitudeFt <= slopeFt(joinsAtNm) + 100) return say(2, why, cmd(turn, `I ${rw.name}`));
    const setup = `descend to ${ft(joinFt)} and ${why.replace(/^ready for/, 'set up')}`, tail = `. Clear the ILS once it is below the glideslope (${ft(slopeFt(joinsAtNm))} where it joins)`;
    return descend || turn ? say(4, setup, cmd(descend || `C ${joinFt / 1000}`, turn), tail) : say(0, setup, undefined, tail);
  };

  // Already on the centreline pointing down the final: nothing to vector.
  if (backNm > 0 && Math.abs(xtkNm) < 0.5 && Math.abs(wrap180(s.headingDeg - rw.headingDeg)) < 20 && s.altitudeFt <= slopeFt(backNm) + 100) {
    return say(2, `lined up on the runway ${rw.name} final`, cmd(`I ${rw.name}`));
  }
  // Abeam the base point or beyond it: on base, cutting in once a 30° cut joins outside the fix.
  const onBase = backNm >= baseNm - 1 && Math.abs(xtkNm) >= BASE_OFFSET_NM * 0.75;
  // A cut that joins outside the intermediate fix from here: 30° if there is room; 45° at most, and
  // only closer in, where waiting on the base leg for a 30° cut is no longer possible.
  if (backNm >= minNm && Math.abs(xtkNm) <= FAR_OFF_CENTRELINE_NM) {
    for (const cut of onBase ? [INTERCEPT_DEG] : [INTERCEPT_DEG, MAX_INTERCEPT_DEG]) {
      const deg = (rw.headingDeg - side * cut + 360) % 360;
      const joinsAtNm = backNm - Math.abs(xtkNm) / tanDeg(cut);
      if (joinsAtNm >= minNm && Math.abs(wrap180(s.headingDeg - deg)) <= MAX_TURN_ONTO_INTERCEPT_DEG) {
        return clearIls(`ready for the runway ${rw.name} ILS with a ${cut}° intercept${cut > INTERCEPT_DEG ? ' (the steepest allowed; turn in earlier next time)' : ''}`, deg, joinsAtNm);
      }
    }
  }
  // A crew still reading back has not flown the last instruction yet: suggesting again would repeat it.
  if (s.pending) return say(0, 'the crew is acting on your last instruction');
  if (Math.abs(xtkNm) > FAR_OFF_CENTRELINE_NM) return vector(`head for a base leg for runway ${rw.name}, ${Math.round(baseNm)} nm out`, toBase);
  if (onBase) return vector(`base leg for runway ${rw.name}; cut in at 30° as it nears the centreline`, (rw.headingDeg - side * 90 + 360) % 360);
  // Too close to join, or past the runway: a downwind on this side of the final, then base.
  const where = backNm >= baseNm - 1 ? `too near the centreline to turn in from here` : backNm < 0 ? `on the far side of runway ${rw.name}` : `too close to join the runway ${rw.name} final (inside ${Math.round(minNm)} nm)`;
  if (Math.abs(xtkNm) < BASE_OFFSET_NM) return vector(`${where}. Turn out to a downwind`, (rw.headingDeg + 180 - side * 45 + 360) % 360);
  return vector(`${where}. Downwind, then base ${Math.round(baseNm)} nm out`, (rw.headingDeg + 180) % 360);
}
